import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import {
  editPropertySchema,
  PROPERTY_TRACKS,
} from "@/modules/property-tracks/utils";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import PropertyDetailsForm from "../add-track-detailed/property-details-form";
import { useEditProperty } from "@/modules/property-tracks/hooks/edit-property.hook";
import { FinishingTypes, Property } from "@/modules/property-tracks/types";
import { TextButton } from "@/modules/common/components/ui/text-button";
import { PencilLine } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import CommonBottomSheet from "@/modules/common/components/ui-kit/bottom-sheets/common-bottom-sheet";
import ModalHeader from "@/modules/common/components/ui-kit/modals/modal-header";
import ModalFooter from "@/modules/common/components/ui-kit/modals/modal-footer";

type EditPropertyFormFields = z.infer<typeof editPropertySchema>;

const EditProperty = ({
  property,
  trackTypeName,
}: {
  property: Property;
  trackTypeName?: string;
}) => {
  const { isMobile } = useScreenSize();
  const [modalOpen, setModalOpen] = useState(false);
  const theme = useTheme();
  const queryClient = useQueryClient();

  const isHospitality = trackTypeName === PROPERTY_TRACKS.HOSPITALITY;

  const defaultValues: Partial<EditPropertyFormFields> = {
    compound: {
      label: property.compound?.name ?? "",
      value: property.compound?.id ?? 0,
    },
    propertyType: {
      label: property.propertyType?.name ?? "",
      value: property.propertyType?.id ?? 0,
    },
    unitId: property.unitId ?? undefined,
    numberOfBedrooms: property.numberOfBedrooms ?? undefined,
    numberOfBathrooms: property.numberOfBathrooms ?? undefined,
    unitArea: property.unitArea ?? undefined,
    phase: property.phase ?? undefined,
    landArea: property.landArea ?? undefined,
    gardenArea: property.gardenArea ?? undefined,
    roofArea: property.roofArea ?? undefined,
    isUnderConstruction: property.isUnderConstruction ?? undefined,
    floorNumber: property.floorNumber ?? undefined,
    numberOfFloors: property.numberOfFloors ?? undefined,
    deliveryDate: property.deliveryDate
      ? new Date(property.deliveryDate)
      : undefined,
    lastInstallmentDate: property.lastInstallmentDate
      ? new Date(property.lastInstallmentDate)
      : undefined,
    readyToMove: property.readyToMove ?? undefined,
    description: property.description ?? undefined,
    websiteListingId: property.websiteListingId ?? undefined,
    finishing: property.finishing
      ? {
          label: FinishingTypes[property.finishing],
          value: property.finishing,
        }
      : undefined,
    amenityIds: new Set(
      property.propertyAmenity?.map((item) => item.amenity.id) || [],
    ),
    latitude: property.latitude ?? undefined,
    longitude: property.longitude ?? undefined,
    address: property.address ?? undefined,
  };

  const editPropertyForm = useForm<EditPropertyFormFields>({
    resolver: zodResolver(editPropertySchema),
    values: defaultValues as EditPropertyFormFields,
  });

  const { handleSubmit: handleSubmitEditProperty, reset: resetEditProperty } =
    editPropertyForm;

  const onSubmitEditPropertyDetailed: SubmitHandler<
    EditPropertyFormFields
  > = async (data) => {
    submitEditProperty(data);
  };

  const mutationEditProperty = useEditProperty();

  const submitEditProperty = (data: EditPropertyFormFields) => {
    mutationEditProperty.mutate(
      {
        uid: property.uid,
        data: {
          compoundId: data.compound.value,
          propertyTypeId: data.propertyType.value,
          unitId: data.unitId,
          numberOfBedrooms: data.numberOfBedrooms,
          numberOfBathrooms: data.numberOfBathrooms,
          unitArea: data.unitArea,
          phase: data.phase,
          landArea: data.landArea,
          gardenArea: data.gardenArea,
          roofArea: data.roofArea,
          ...(!isHospitality && {
            isUnderConstruction: data.isUnderConstruction,
          }),
          floorNumber: data.floorNumber,
          numberOfFloors: data.numberOfFloors,
          deliveryDate: data.readyToMove ? undefined : data.deliveryDate,
          lastInstallmentDate: data.lastInstallmentDate,
          readyToMove: data.readyToMove,
          finishing: data.finishing?.value,
          websiteListingId: data.websiteListingId,
          description: data.description ?? undefined,
          amenityIds: [...(data.amenityIds?.values() ?? [])],
          latitude: data.latitude,
          longitude: data.longitude,
          address: data.address,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["track"] });
          queryClient.invalidateQueries({ queryKey: ["tracks"] });
          setModalOpen(false);
          resetEditProperty();
          showToast({
            type: ToastType.SUCCESS,
            message: "Property updated successfully",
            toastId: TOAST_IDS.EDIT_PROPERTY_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.EDIT_PROPERTY_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      },
    );
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    resetEditProperty();
  };

  const handleModalOpen = () => {
    setModalOpen(true);
    resetEditProperty(defaultValues);
  };

  const { readonly } = useReadonlyContext();

  return (
    <>
      <TextButton
        disabled={readonly}
        onClick={handleModalOpen}
        sx={{ display: "flex", gap: 0.5, alignItems: "bottom" }}
      >
        <PencilLine size={16} /> Edit
      </TextButton>
      {isMobile ? (
        <CommonBottomSheet
          isOpen={modalOpen}
          onClose={handleCloseModal}
          snapPoints={[0.8, 0.8]}
        >
          <ModalHeader title="Edit Property" onClose={handleCloseModal} />
          <Box overflow="auto" width="100%" height="90%">
            <Box p={2}>
              <PropertyDetailsForm
                form={editPropertyForm}
                trackTypeName={trackTypeName}
                buildingAmenities={property.parentBuildingAmenities?.map(
                  (pa) => pa.amenity,
                )}
                isSubProperty={property.isSubProperty}
              />
            </Box>
            <Box position="sticky" bottom={0} width="100%">
              <ModalFooter
                confirmButtonText="Save"
                cancelButtonText="Cancel"
                onConfirm={handleSubmitEditProperty(
                  onSubmitEditPropertyDetailed,
                )}
                isLoading={mutationEditProperty.isPending}
                onCancel={handleCloseModal}
              />
            </Box>
          </Box>
        </CommonBottomSheet>
      ) : (
        <EditFieldModal
          open={modalOpen}
          setOpen={setModalOpen}
          header="Edit Property"
          width={480}
          handleConfirm={handleSubmitEditProperty(onSubmitEditPropertyDetailed)}
          handleCloseModal={handleCloseModal}
          isLoading={mutationEditProperty.isPending}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: theme.nawy.spacing.md,
            }}
          >
            <PropertyDetailsForm
              form={editPropertyForm}
              trackTypeName={trackTypeName}
              buildingAmenities={property.parentBuildingAmenities?.map(
                (pa) => pa.amenity,
              )}
              isSubProperty={property.isSubProperty}
            />
            {!isHospitality && (
              <Controller
                control={editPropertyForm.control}
                name={"isUnderConstruction"}
                render={({ field }) => (
                  <Box>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Under Construction"
                      />
                    </FormGroup>
                  </Box>
                )}
              />
            )}
          </Box>
        </EditFieldModal>
      )}
    </>
  );
};

export default EditProperty;
